<template>
  <BModal v-model="isVisible"
          size="lg"
          centered
          scrollable
          no-close-on-backdrop
          body-class="p-0"
          @keydown.enter="apply"
          header-class="modal-header-full"
          hide-header-close
          content-class="rounded-xl overflow-hidden shadow-2xl border-0">
    <template #title>
      <div class="header-layout">
        <div class="header-left">
          <h5 class="h5 mb-0 modal-title">{{ placeholder || $t('Webportal.Modal.SelectItems') }}</h5>
          <p class="modal-description">
            {{ $t('Webportal.HierarchicalSelection') }}
          </p>
        </div>
        <div class="header-actions">
          <BButton variant="outline-secondary" size="sm" @click="close">{{ $t('Webportal.Cancel') }}</BButton>
          <BButton variant="primary" size="sm" @click="apply">{{ $t('Webportal.Apply') }}</BButton>
        </div>
      </div>
    </template>

    <!-- Footer -->
    <template #footer>
      <div class="text-muted small me-auto">{{ visibleLeafCount }} / {{ totalLeafCount }} {{ $t('Webportal.FilterModal.Visible')}}</div>
      <div class="text-muted small">{{ $t('Webportal.CountOptionsSelected', { count: countCheckedLeaves(treeData) }) }}</div>
    </template>

    <!-- Body -->
    <div class="flex flex-col h-full">
      <div class="p-4 flex-grow-1 overflow-hidden d-flex flex-column">
        <!-- Search -->
        <div class="position-relative mb-3">
          <Search class="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" :size="16" />
          <input  type="text"
                  class="form-control ps-5"
                  :placeholder="`${$t('Webportal.Search')}...`"
                  v-model="searchQuery">
        </div>

        <!-- Tree View -->
        <RecycleScroller  :key="scrollerKey"
                          class="rounded scroll-y"
                          style="min-height: 50vh; max-height: 50vh; overflow-y: auto;"
                          :items="flattenedTreeData"
                          :item-size="32"
                          key-field="id"
                          v-slot="{ item }">

          <div class="border-bottom last-border-bottom-0" :style="{ paddingLeft: (item.level * 24) + 'px' }">
            <div class="p-2 d-flex align-items-center">
              <button v-if="item.hasChildren"
                      class="btn btn-sm btn-icon p-1 me-2 text-muted rounded"
                      @click="toggleExpand(item)">
                <ChevronDown v-if="item.expanded" :size="16" />
                <ChevronRight v-else :size="16" />
              </button>
              <button v-else class="btn btn-sm btn-icon p-1 me-2 invisible">
                <ChevronRight :size="16" />
              </button>

              <div class="d-flex align-items-center flex-grow-1 cursor-pointer"
                  @click.stop="toggleNode(item, item.parent, item.grandparent)">
                <div class="custom-checkbox me-2" :class="{ 'checked': item.checked, 'indeterminate': item.indeterminate }">
                  <Check v-if="item.checked" :size="12" class="text-white" />
                  <Minus v-else-if="item.indeterminate" :size="12" class="text-white" />
                </div>
                <span class="user-select-none" :class="{ 'fw-medium': item.level === 0 }">{{ item.label }}</span>
              </div>
            </div>
          </div>
        </RecycleScroller>
      </div>
    </div>
  </BModal>
</template>

<script>
import { Search, ChevronDown, ChevronRight, Check, Minus } from 'lucide-vue-next';
import { countCheckedLeaves, countAllLeaves } from '@/utils/treeHelpers';
import { RecycleScroller } from 'vue-virtual-scroller';
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';

export default {
  name: 'ModalFilter',

  components: {
    Search,
    ChevronDown,
    ChevronRight,
    Check,
    Minus,
    RecycleScroller
  },

  props: {
    modelValue:     { type: Boolean, default: false },
    filterName:     { type: String, default: '' },
    placeholder:    { type: String, default: null },
    initialData:    { type: Array, default: () => [] },
    cachedOptions:  { type: Array, default: null },
    showAllOptions: { type: Boolean, default: false }
  },

  emits: ['update:modelValue', 'apply'],

  data() {
    return {
      searchQuery: '',
      debouncedSearchQuery: '',
      searchDebounceTimer: null,
      treeData: [],
      initialCheckedSignature: '',
      scrollerKey: 0,
    };
  },

  computed: {
    isVisible: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      }
    },

    flattenedTreeData() {
      const flattened = [];

      const flatten = (nodes, level = 0, parent = null, grandparent = null) => {
        nodes.forEach(node => {
          flattened.push({
            originalNode: node,
            id: node.id,
            label: node.label,
            checked: node.checked,
            indeterminate: node.indeterminate,
            expanded: node.expanded,
            level,
            parent,
            grandparent,
            hasChildren: !!(node.children && node.children.length > 0)
          });

          if (node.expanded && node.children) {
            flatten(node.children, level + 1, node, parent);
          }
        });
      };

      const dataToFlatten = this.debouncedSearchQuery ? this.filteredTreeData : this.treeData;
      flatten(dataToFlatten);

      return flattened;
    },

    filteredTreeData() {
      if (!this.debouncedSearchQuery) {
        return this.treeData;
      }

      const query = this.debouncedSearchQuery.toLowerCase();

      const filterNode = (node) => {
        const nodeMatches = node.label.toLowerCase().includes(query);
        let matchingChildren = [];

        if (node.children) {
          matchingChildren = node.children
            .map(child => filterNode(child))
            .filter(child => child !== null);
        }

        if (nodeMatches || matchingChildren.length > 0) {
          node.expanded = true;
          return node;
        }

        return null;
      };

      return this.treeData
        .map(group => filterNode(group))
        .filter(group => group !== null);
    },

    visibleLeafCount() {
      return this.countAllLeaves(this.filteredTreeData);
    },

    totalLeafCount() {
      return this.countAllLeaves(this.treeData);
    }
  },

  methods: {
    countCheckedLeaves, countAllLeaves,

    initTreeData(newVal) {
      this.searchQuery = '';
      this.debouncedSearchQuery = '';
      if (!newVal) return;
      let items;
      if (this.showAllOptions && this.cachedOptions) {
        const checkedIds = this.getCheckedLeafIds(JSON.parse(JSON.stringify(newVal)));
        items = JSON.parse(JSON.stringify(this.cachedOptions));
        this.applyCheckedIds(items, checkedIds);
      } else {
        items = JSON.parse(JSON.stringify(newVal));
        if (items && !Array.isArray(items)) items = [items];
      }
      const expandAll = (nodes) => {
        nodes.forEach(node => {
          if (node.children && node.children.length > 0) {
            node.expanded = true;
            expandAll(node.children);
          }
        });
      };
      expandAll(items);
      this.reconcileParentStates(items);
      this.treeData = items;
      this.initialCheckedSignature = this.getCheckedFlatIds(items).sort().join(',');
      this.scrollerKey++;
    },

    close() {
      this.initTreeData(this.initialData);
      this.isVisible = false;
    },

    apply() {
      const selected = [];
      const collectCheckedLeaves = (nodes) => {
        nodes.forEach(node => {
          if (node.children && node.children.length > 0) {
            collectCheckedLeaves(node.children);
          } else if (node.checked) {
            selected.push(node);
          }
        });
      };
      collectCheckedLeaves(this.treeData);
      const flatIds = selected.flatMap(i => Array.isArray(i.id) ? i.id : [i.id]);

      const currentSignature = [...flatIds].sort().join(',');
      if (currentSignature === this.initialCheckedSignature) {
        this.close();
        return;
      }

      this.$emit('apply', this.filterName, this.treeData, flatIds);
      this.close();
    },

    toggleExpand(item) {
      const node = item.originalNode || item;
      node.expanded = !node.expanded;
    },

    toggleNode(item, parent = null, grandparent = null) {
      // Brand-spezifische Logik: Selektion nur innerhalb eines Hauptknotens erlaubt
      if (this.filterName === 'Brand') {
        const clickedRoot = grandparent || parent || (item.originalNode || item);
        const deselectAll = (nodes) => {
          nodes.forEach(node => {
            node.checked = false;
            node.indeterminate = false;
            if (node.children) deselectAll(node.children);
          });
        };
        this.treeData.forEach(rootNode => {
          if (rootNode !== clickedRoot) {
            deselectAll([rootNode]);
          }
        });
      }

      const node = item.originalNode || item;
      const targetState = !node.checked;

      // Waves: maximale Auswahl von 4 Einträgen
      if (this.filterName === 'Waves' && targetState === true) {
        if (this.countCheckedLeaves(this.treeData) >= 4) {
          return;
        }
      }

      node.checked = targetState;
      node.indeterminate = false;

      // Toggle all children recursively
      if (node.children) {
        const toggleChildren = (children) => {
          children.forEach(child => {
            child.checked = targetState;
            child.indeterminate = false;
            if (child.children) {
              toggleChildren(child.children);
            }
          });
        };
        toggleChildren(node.children);
      }

      // Update parent states
      if (parent) {
        this.updateNodeState(parent);
        if (grandparent) {
          this.updateNodeState(grandparent);
        }
      }

      // Brand: Fallback – wenn nichts mehr selektiert, ersten Unterknoten + alle seine Leafs selektieren
      if (this.filterName === 'Brand' && this.countCheckedLeaves(this.treeData) === 0) {
        const firstRoot = this.treeData[0];
        if (firstRoot && firstRoot.children && firstRoot.children.length > 0) {
          const firstChild = firstRoot.children[0];
          firstChild.checked = true;
          firstChild.indeterminate = false;
          if (firstChild.children) {
            const selectAll = (nodes) => {
              nodes.forEach(n => {
                n.checked = true;
                n.indeterminate = false;
                if (n.children) selectAll(n.children);
              });
            };
            selectAll(firstChild.children);
          }
          this.updateNodeState(firstRoot);
        }
      }
    },

    updateNodeState(node) {
      if (!node.children || node.children.length === 0) return;

      const allChecked = node.children.every(c => c.checked && !c.indeterminate);
      const someChecked = node.children.some(c => c.checked || c.indeterminate);

      node.checked = allChecked;
      node.indeterminate = someChecked && !allChecked;
    },

    reconcileParentStates(nodes) {
      nodes.forEach(node => {
        if (node.children && node.children.length > 0) {
          this.reconcileParentStates(node.children);
          this.updateNodeState(node);
        }
      });
    },

    getCheckedLeafIds(nodes) {
      const ids = new Set();
      const collect = (nodes) => {
        nodes.forEach(node => {
          if (node.children && node.children.length > 0) {
            collect(node.children);
          } else if (node.checked) {
            if (Array.isArray(node.id)) node.id.forEach(id => ids.add(id));
            else ids.add(node.id);
          }
        });
      };
      collect(nodes);
      return ids;
    },

    applyCheckedIds(nodes, checkedIds) {
      nodes.forEach(node => {
        if (node.children && node.children.length > 0) {
          this.applyCheckedIds(node.children, checkedIds);
        } else {
          const nodeIds = Array.isArray(node.id) ? node.id : [node.id];
          node.checked = nodeIds.some(id => checkedIds.has(id));
          node.indeterminate = false;
        }
      });
    },

    getCheckedFlatIds(nodes) {
      const ids = [];
      const collect = (nodes) => {
        nodes.forEach(node => {
          if (node.children && node.children.length > 0) {
            collect(node.children);
          } else if (node.checked) {
            if (Array.isArray(node.id)) ids.push(...node.id);
            else ids.push(node.id);
          }
        });
      };
      collect(nodes);
      return ids;
    }
  },

  watch: {
    searchQuery() {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.debouncedSearchQuery = this.searchQuery;
      }, 300);
    },

    initialData: {
      handler(newVal) {
        this.initTreeData(newVal);
      },
      immediate: true
    }
  }
};
</script>

<style lang="scss" scoped>
.custom-checkbox {
  width: 18px;
  height: 18px;
  border: 2px solid var(--bs-gray-400);
  border-radius: var(--radius-xs);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &.checked, &.indeterminate {
    background-color: var(--bs-primary); // Primary color
    border-color: var(--bs-primary);
  }
}

.last-border-bottom-0:last-child {
  border-bottom: 0 !important;
}

.btn-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}


</style>
